import { create } from 'zustand';
import { devtools } from 'zustand/middleware';
import {
  cognitoAuthService,
  tokenManager,
  type AuthUser,
  type ChallengeResult,
  AuthError,
} from '@/lib/auth';

type ForgotPasswordStep = 'email' | 'code' | 'success';

interface AuthState {
  // State
  user: AuthUser | null;
  isAuthenticated: boolean;
  isLoading: boolean;
  error: string | null;

  // Challenge state (for MFA, new password, etc.)
  challenge: ChallengeResult | null;

  // Forgot password state
  forgotPasswordStep: ForgotPasswordStep | null;
  forgotPasswordEmail: string | null;

  // Actions
  signIn: (email: string, password: string) => Promise<void>;
  signOut: () => Promise<void>;
  checkAuth: () => Promise<void>;
  respondToChallenge: (responses: Record<string, string>) => Promise<void>;
  forgotPassword: (email: string) => Promise<void>;
  confirmForgotPassword: (email: string, code: string, newPassword: string) => Promise<void>;
  clearError: () => void;
  clearChallenge: () => void;
  clearForgotPassword: () => void;
}

export const useAuthStore = create<AuthState>()(
  devtools(
    (set, get) => ({
      // Initial state
      user: null,
      isAuthenticated: false,
      isLoading: true, // Start loading to check initial auth
      error: null,
      challenge: null,
      forgotPasswordStep: null,
      forgotPasswordEmail: null,

      // Sign in
      signIn: async (email, password) => {
        set({ isLoading: true, error: null, challenge: null });

        try {
          const response = await cognitoAuthService.signIn(email, password);

          if (response.type === 'challenge') {
            // Need to handle challenge (MFA, new password, etc.)
            set({
              isLoading: false,
              challenge: response.data,
            });
          } else {
            // Success
            set({
              user: response.data.user,
              isAuthenticated: true,
              isLoading: false,
            });
          }
        } catch (error) {
          const message =
            error instanceof AuthError
              ? error.message
              : 'An unexpected error occurred';
          set({
            error: message,
            isLoading: false,
          });
          throw error;
        }
      },

      // Respond to challenge
      respondToChallenge: async (responses) => {
        const { challenge } = get();
        if (!challenge) {
          throw new Error('No active challenge');
        }

        set({ isLoading: true, error: null });

        try {
          const response = await cognitoAuthService.respondToChallenge(
            challenge.challengeName,
            challenge.session,
            responses
          );

          if (response.type === 'challenge') {
            // Another challenge
            set({
              isLoading: false,
              challenge: response.data,
            });
          } else {
            // Success
            set({
              user: response.data.user,
              isAuthenticated: true,
              isLoading: false,
              challenge: null,
            });
          }
        } catch (error) {
          const message =
            error instanceof AuthError
              ? error.message
              : 'An unexpected error occurred';
          set({
            error: message,
            isLoading: false,
          });
          throw error;
        }
      },

      // Sign out
      signOut: async () => {
        set({ isLoading: true });

        try {
          await cognitoAuthService.signOut();
        } finally {
          set({
            user: null,
            isAuthenticated: false,
            isLoading: false,
            error: null,
            challenge: null,
          });
        }
      },

      // Check if already authenticated (on app load)
      checkAuth: async () => {
        set({ isLoading: true });

        try {
          // Check if we have valid tokens
          if (tokenManager.isExpired()) {
            // Try to refresh
            const refreshToken = tokenManager.getRefreshToken();
            if (refreshToken) {
              try {
                const result = await cognitoAuthService.refreshTokens();
                set({
                  user: result.user,
                  isAuthenticated: true,
                  isLoading: false,
                });
                return;
              } catch {
                // Refresh failed, user needs to sign in again
              }
            }

            set({
              user: null,
              isAuthenticated: false,
              isLoading: false,
            });
            return;
          }

          // Tokens are valid, get user info
          const user = await cognitoAuthService.getCurrentUser();
          if (user) {
            set({
              user,
              isAuthenticated: true,
              isLoading: false,
            });
          } else {
            set({
              user: null,
              isAuthenticated: false,
              isLoading: false,
            });
          }
        } catch {
          set({
            user: null,
            isAuthenticated: false,
            isLoading: false,
          });
        }
      },

      // Forgot password - send verification code
      forgotPassword: async (email) => {
        set({ isLoading: true, error: null });

        try {
          await cognitoAuthService.forgotPassword(email);
          set({
            isLoading: false,
            forgotPasswordStep: 'code',
            forgotPasswordEmail: email,
          });
        } catch (error) {
          const message =
            error instanceof AuthError
              ? error.message
              : 'An unexpected error occurred';
          set({
            error: message,
            isLoading: false,
          });
          throw error;
        }
      },

      // Confirm forgot password - verify code and set new password
      confirmForgotPassword: async (email, code, newPassword) => {
        set({ isLoading: true, error: null });

        try {
          await cognitoAuthService.confirmForgotPassword(email, code, newPassword);
          set({
            isLoading: false,
            forgotPasswordStep: 'success',
          });
        } catch (error) {
          const message =
            error instanceof AuthError
              ? error.message
              : 'An unexpected error occurred';
          set({
            error: message,
            isLoading: false,
          });
          throw error;
        }
      },

      // Clear error
      clearError: () => set({ error: null }),

      // Clear challenge
      clearChallenge: () => set({ challenge: null }),

      // Clear forgot password state
      clearForgotPassword: () => set({ forgotPasswordStep: null, forgotPasswordEmail: null, error: null }),
    }),
    { name: 'AuthStore' }
  )
);

// Listen for auth:expired events from API client
if (typeof window !== 'undefined') {
  window.addEventListener('auth:expired', () => {
    useAuthStore.getState().signOut();
  });
}
